import { ReactNode, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRPCClientErrorLike, httpLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import type { ProcedureUseQuery } from '@trpc/react-query/dist/createTRPCReact';
import type { DefinedUseTRPCQueryResult, UseTRPCMutationOptions, UseTRPCMutationResult } from '@trpc/react-query/dist/shared';
import type { AnyMutationProcedure, AnyQueryProcedure, inferProcedureInput, inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { inferTransformedProcedureOutput } from '@trpc/server/shared';
import type { ApiRouter } from 'api/routes/trcpRouter';

export type RouteInputs = inferRouterInputs<ApiRouter>;
export type RouteOutputs = inferRouterOutputs<ApiRouter>;

const httpLinkWithStatusFilter = httpLink({
  url: '/api/trpc',

  // This is a hacky way to pass the "allowedStatuses" array to the fetch function without rebuilding the entire httpLink
  headers: ({ op }) => {
    if (op.context.allowedStatuses) {
      return { 'allowed-statuses': ((op.context.allowedStatuses || []) as string[]).join(',') };
    }
    return {};
  },

  fetch: (url, options) => {
    // Extract the "allowedStatuses" array from headers
    let allowedStatuses = ['any'];
    if (options?.headers && 'allowed-statuses' in options.headers) {
      allowedStatuses = options.headers['allowed-statuses'].split(',');
      delete options.headers['allowed-statuses'];
    }

    // Inject a check for the API response status into the fetch function
    return fetch(url, options).then(
      (response) =>
        new Promise((resolve, reject) =>
          response
            .json()
            .then((json) => {
              // A hacky way of allowing consuming the response.json again
              response.json = async () => json;

              // This is not a standard API response, do nothing
              if (!json.result?.data?.status) return resolve(response);

              if (allowedStatuses[0] !== 'any' && !allowedStatuses.includes(json.result?.data?.status)) {
                return reject(
                  new Error(
                    `Failed to fetch ${url}, invalid status "${
                      json.result?.data?.status
                    }" returned. Allowed statuses are [${allowedStatuses.join(', ')}].`
                  )
                );
              }
              resolve(response);
            })
            .catch((e) => {
              return reject(e);
            })
        )
    );
  },
});

export const trpc = createTRPCReact<ApiRouter>();

export const TRPCProvider = ({ children }: { children: ReactNode }) => {
  const [queryClient] = useState(() => new QueryClient());
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [httpLinkWithStatusFilter],
    })
  );

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
};

export const useApi = () => {
  const utils = trpc.useUtils();
  return utils.client;
};

/**
 * Custom wrapper for the built-in useQuery hook that accepts "allowedStatuses" filter as an option.
 * The return value is scoped to the "allowedStatuses" filter. Only the "ok" response is allowed by default.
 */
export const useQuery = <
  TProcedure extends AnyQueryProcedure,
  TPath extends string,
  TData extends inferTransformedProcedureOutput<TProcedure> = inferTransformedProcedureOutput<TProcedure>,
  TAllowedStatuses extends readonly string[] = ['ok'],
>(
  procedure: { useQuery: ProcedureUseQuery<TProcedure, TPath> },
  input: Parameters<ProcedureUseQuery<TProcedure, TPath>>[0],
  opts: Parameters<ProcedureUseQuery<TProcedure, TPath>>[1] & { allowedStatuses?: TAllowedStatuses } = {}
) => {
  const { allowedStatuses = ['ok'] as unknown as TAllowedStatuses, ...queryOpts } = opts;

  // Use the built-in useQuery hook but inject the allowedStatuses to the tRPC context
  const query = procedure.useQuery(input, {
    ...queryOpts,
    trpc: { ...(queryOpts.trpc || {}), context: { ...(queryOpts.trpc?.context || {}), allowedStatuses } },
  });

  // Filter the return type based on the allowedStatuses array
  type TResult = DefinedUseTRPCQueryResult<TData, TRPCClientErrorLike<TProcedure>>;
  type TResultData = Extract<TResult['data'], { status: TAllowedStatuses[number] }>;

  return query as Omit<TResult, 'data'> & { data: TResultData | null };
};

/**
 * Custom wrapper for the built-in useMutation hook.
 * Allows all statuses to be returned.
 */
export const useMutation = <TProcedure extends AnyMutationProcedure>(
  procedure: {
    useMutation: (
      opts?: UseTRPCMutationOptions<
        inferProcedureInput<TProcedure>,
        TRPCClientErrorLike<TProcedure>,
        inferTransformedProcedureOutput<TProcedure>,
        any
      >
    ) => UseTRPCMutationResult<
      inferTransformedProcedureOutput<TProcedure>,
      TRPCClientErrorLike<TProcedure>,
      inferProcedureInput<TProcedure>,
      any
    >;
  },
  opts: UseTRPCMutationOptions<
    inferProcedureInput<TProcedure>,
    TRPCClientErrorLike<TProcedure>,
    inferTransformedProcedureOutput<TProcedure>,
    any
  > = {}
) => {
  // Use the built-in useQuery hook but inject the allowedStatuses to the tRPC context
  const mutation = procedure.useMutation({
    ...opts,
    trpc: { ...(opts.trpc || {}), context: { ...(opts.trpc?.context || {}), allowedStatuses: ['any'] } },
  });

  return mutation;
};

/**
 * Listen to validation errors on a specific query.
 */
// TODO: Waiting for tRPC v11
// export const useValidationError = <TProcedure extends AnyMutationProcedure>(
//   procedure: {
//     useMutation: (
//       opts?: UseTRPCMutationOptions<
//         inferProcedureInput<TProcedure>,
//         TRPCClientErrorLike<TProcedure>,
//         inferTransformedProcedureOutput<TProcedure>,
//         any
//       >
//     ) => UseTRPCMutationResult<
//       inferTransformedProcedureOutput<TProcedure>,
//       TRPCClientErrorLike<TProcedure>,
//       inferProcedureInput<TProcedure>,
//       any
//     >;
//   },
//   path: string
// ) => {
//   const mutationKey = getQueryKey(procedure);

//   const state = useMutationState({
//     filters: { mutationKey },
//     select: (mutation) => mutation.state,
//   });

//   console.log('state', state);
// };
